import { JetView } from "webix-jet";
import { dateFromISO, dateCommonFormat } from "../../helpers/dateFormat";
import { statusBadge, userAvatar } from "helpers/templates";

import CredentialsWin from "views/users/credentials";

export default class UsersView extends JetView {
	config() {
		const _ = this.app.getService("locale")._;

		const buttons = {
			view: "form",
			margin: 10,
			cols: [
				{},
				{
					width: 120,
					value: _("Delete"),
					view: "button",
					click: () => this.Delete(),
				},
				{
					width: 120,
					value: _("Save"),
					view: "button",
					css: "webix_primary",
					click: () => this.Save(),
				},
			],
		};

		const info = {
			type: "clean",
			rows: [
				buttons,
				{
					view: "form",
					localId: "form",
					margin: 20,
					css: "webix_um_edit_form",
					scroll: "auto",
					elementsConfig: {
						labelAlign: "right",
						labelWidth: 130,
					},
					rows: [
						{
							cols: [
								{
									view: "text",
									label: _("Full Name"),
									localId: "focus",
									name: "name",
									gravity: 2,
									maxWidth: 550,
									minWidth: 420,
								},
								{},
							],
						},
						{
							cols: [
								{
									view: "text",
									label: _("Email"),
									name: "email",
									gravity: 2,
									maxWidth: 550,
									minWidth: 420,
								},
								{},
							],
						},

						{
							cols: [
								{
									view: "forminput",
									label: _("Avatar"),
									body: {
										localId: "avatar",
										css: "webix_um_form_avatar",
										width: 95,
										height: 95,
										borderless: true,
										template: user => {
											return `${userAvatar(user, "webix_um_avatarbox")}`;
										},
									},
								},
								{
									rows: [
										{},
										{
											view: "uploader",
											localId: "avatarUpl",
											accept: "image/*",
											autowidth: true,
											autosend: true,
											label: _("Upload new photo"),
											type: "icon",
											icon: "umi-upload",
											css: "webix_transparent webix_um_uploader",
											on: {
												onAfterFileAdd: fileData => this.UpdateAvatar(fileData),
												onUploadComplete: response =>
													this.UpdateAvatar(response),
											},
										},
									],
								},
								{},
							],
						},
						{
							// because for now we receive a number `0` and have only two known states
							view: "switch",
							name: "status",
							localId: "status",
							label: _("Status"),
							css: "webix_um_status_switch",
							on: {
								onChange: value => this.UpdateBadge(value),
							},
						},
						{
							view: "forminput",
							label: _("Registered"),
							body: {
								view: "label",
								localId: "registeredDate",
								css: "webix_um_text_normal",
							},
						},
						{
							view: "forminput",
							label: _("Last visited"),
							body: {
								view: "label",
								localId: "visitedDate",
								css: "webix_um_text_normal",
							},
						},
						{
							visibleBatch: "def",
							localId: "credentials",
							css: "webix_um_credentials",
							cols: [
								{
									view: "label",
									label: _("Credentials"),
									width: 130,
									css: "webix_um_form_label",
								},
								{
									batch: "def",
								},
								{
									batch: "no-credentials",
									cols: [
										{
											view: "button",
											label: _("Add credentials"),
											width: 160,
											click: () => this.AddCredentials(),
										},
										{},
									],
								},
								{
									batch: "credentials",
									margin: 5,
									cols: [
										{
											view: "button",
											css: "webix_transparent",
											label: _("Reset password"),
											width: 140,
											click: () => this.ResetPassword(),
										},
										{
											view: "button",
											css: "webix_transparent",
											label: _("Delete credentials"),
											width: 150,
											click: () => this.DeleteCredentials(),
										},
										{},
									],
								},
							],
						},
						{
							cols: [
								{
									view: "textarea",
									name: "details",
									label: _("Details"),
									height: 128,
									gravity: 2,
									maxWidth: 550,
									minWidth: 420,
								},
								{},
							],
						},
						{},
					],
				},
			],
		};
		return info;
	}

	urlChange() {
		this.ID = this.getParam("id", true) * 1;
		this.ShowCredentials();
		this.$$("avatarUpl").config.upload = this.app
			.getService("backend")
			.avatar(this.ID);

		this.app
			.getService("local")
			.users()
			.then(data => {
				const user = data.getItem(this.ID);
				this.$$("form").setValues(user);
				this.$$("focus").focus();
				// render initial templates
				this.$$("registeredDate").setValue(this.FormatDate(user.registered));
				this.$$("visitedDate").setValue(this.FormatDate(user.visited));
				this.$$("avatar").setValues(user);
			});
	}

	Save() {
		const form = this.$$("form");

		this.app.getService("operations").updateUser(this.ID, form.getValues());
	}

	Delete() {
		const _ = this.app.getService("locale")._;
		webix
			.confirm({
				title: _("Delete"),
				ok: _("Delete"),
				cancel: _("Cancel"),
				text: _("Are you sure to delete this user?"),
			})
			.then(() => {
				this.app.getService("operations").deleteUser(this.ID);
				this.app.show("/top/users/_hidden");
			});
	}

	UpdateBadge(value) {
		const _ = this.app.getService("locale")._;
		const control = this.$$("status");

		let html = statusBadge(value, _);
		control.config.labelRight = html;
		control.refresh();
	}

	UpdateAvatar(data) {
		if (data.status === "client" && data.file) {
			// client-side preview
			const reader = new FileReader();
			reader.onload = () => {
				this.SetLocalAvatar(reader.result);
			};
			reader.readAsDataURL(data.file);
		} else if (data.status === "server") {
			// refresh picture after uploading, update data in form
			const avatar = data.value;
			this.$$("form").setValues({ avatar }, true);
			this.SetLocalAvatar(avatar);
		}
	}

	SetLocalAvatar(avatar) {
		this.$$("avatar").setValues({ avatar });
		// local update. Otherwise, we need to send _all_ user data to the server
		this.app
			.getService("local")
			.users()
			.then(data => {
				data.updateItem(this.ID, { avatar });
			});
	}

	FormatDate(date) {
		if (date) {
			date = dateFromISO(date);
			date = dateCommonFormat(date);
		}
		return date || "";
	}

	ShowCredentials() {
		let p = this.app.getService("backend").credentials(this.ID);
		p.then(data => {
			const batch = data.length ? "credentials" : "no-credentials";
			this.credID = data.length ? data[0].id : null;
			this.$$("credentials").showBatch(batch);
		});
	}

	AddCredentials() {
		const _ = this.app.getService("locale")._;
		if (!this.CheckEmail())
			return webix.alert({
				type: "alert-error",
				title: _("Error: no email"),
				text: _("Please submit an email first!"),
			});
		const data = { user_id: this.ID, source: 1, record: "test" };
		let p = this.app.getService("backend").addCredentials(this.ID, data);
		p.then(response => {
			if (response.id) {
				this.credID = response.id;
				this.$$("credentials").showBatch("credentials");
			}
		});
	}

	CheckEmail() {
		const users = this.app.getService("local").users(true);
		return users.getItem(this.ID).email;
	}

	ResetPassword() {
		this.ui(CredentialsWin).Show(this.credID);
	}

	DeleteCredentials() {
		const _ = this.app.getService("locale")._;
		webix
			.confirm({
				title: _("Delete"),
				ok: _("Delete"),
				cancel: _("Cancel"),
				text: _("Are you sure to delete credentials?"),
			})
			.then(() => {
				let p = this.app
					.getService("backend")
					.deleteCredentials(this.ID, this.credID);
				p.then(data => {
					if (data.id) this.ShowCredentials();
				});
			});
	}
}
